"use client";

import { useCallback, useEffect, useState } from "react";
import { ALargeSmall, Send, Smile } from "lucide-react";
import { Button } from "ui";
import { EditorToolbar, RichTextEditor, useRichEditor } from "editor";
import EmojiPickerButton from "@/components/emoji-picker-button";

interface MessageComposerProps {
  channelId: string;
  parentMessageId?: string;
  /**
   * Channel name shown in bold inside the placeholder (e.g. "Message #general").
   * Takes precedence over `placeholder` when set.
   */
  channelName?: string;
  /** Plain placeholder text — used when `channelName` is not provided. */
  placeholder?: string;
  onSend: (content: string) => Promise<void>;
  disabled?: boolean;
  /** When set and `disabled` is true, shows this message above the composer. */
  disabledMessage?: string;
  compact?: boolean;
}

export default function MessageComposer({
  channelId,
  parentMessageId,
  channelName,
  placeholder = "Send a message…",
  onSend,
  disabled = false,
  disabledMessage,
  compact = false,
}: MessageComposerProps) {
  const [isFocused, setIsFocused] = useState(false);
  const [showToolbar, setShowToolbar] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);

  // When a channelName is provided we render our own rich placeholder
  // overlay (with the channel name in bold), so we pass an empty string to
  // TipTap to disable its built-in placeholder. Otherwise let TipTap handle it.
  const editor = useRichEditor({
    preset: "messaging",
    placeholder: channelName ? "" : placeholder,
    editable: !disabled,
    onUpdate: (ed) => setIsEmpty(ed.isEmpty),
    onFocus: () => setIsFocused(true),
    onBlur: () => setIsFocused(false),
  });

  // Initialize empty state once the editor is ready
  useEffect(() => {
    if (editor) setIsEmpty(editor.isEmpty);
  }, [editor]);

  const handleSend = useCallback(async () => {
    if (!editor) return;
    const text = editor.getText().trim();
    if (!text) return;
    const html = editor.getHTML();
    editor.commands.clearContent();
    await onSend(html);
  }, [editor, onSend]);

  const handleEmojiInsert = useCallback(
    (native: string) => {
      if (!editor) return;
      editor.chain().focus().insertContent(native).run();
    },
    [editor],
  );

  // Enter to send, Shift+Enter for new line
  useEffect(() => {
    if (!editor) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter" && !e.shiftKey) {
        // Inside a list, Enter should create a new list item (or exit the
        // list when empty) — let TipTap handle it instead of sending.
        if (editor.isActive("bulletList") || editor.isActive("orderedList")) {
          return;
        }
        e.preventDefault();
        handleSend();
      }
    };
    const el = editor.view.dom;
    el.addEventListener("keydown", handler);
    return () => el.removeEventListener("keydown", handler);
  }, [editor, handleSend]);

  // The placeholder sits in the same spot as the editor's first line of text.
  // It must clear the toolbar header (40px) when present.
  const placeholderTopOffset = showToolbar ? "top-[48px]" : "top-2";

  return (
    <div className={compact ? "mx-0" : "mx-5 mb-4 mt-3"}>
      {disabled && disabledMessage && (
        <p className="mb-2 text-center text-xs text-muted-foreground">
          {disabledMessage}
        </p>
      )}
      <div
        className={`relative${disabled ? " pointer-events-none opacity-50" : ""}`}
      >
        {isEmpty && channelName && (
          <div
            className={`pointer-events-none absolute left-3 ${placeholderTopOffset} z-10 select-none text-sm text-grunge40 dark:text-grunge60`}
          >
            Message <span className="font-bold">#{channelName}</span>
          </div>
        )}
        <RichTextEditor
          editor={editor}
          isFocused={isFocused}
          minHeight={40}
          maxHeight={200}
          header={
            showToolbar
              ? (ed) => (
                  // Prevent toolbar clicks from stealing the editor's text
                  // selection on mousedown, so formatting applies to the
                  // highlighted text instead of a collapsed caret.
                  <div onMouseDown={(e) => e.preventDefault()}>
                    <EditorToolbar editor={ed} />
                  </div>
                )
              : undefined
          }
          footer={() => (
            <div className="flex items-center justify-between px-2 py-1.5">
              <div className="flex items-center gap-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowToolbar((prev) => !prev)}
                  icon={<ALargeSmall />}
                  title="Formatting"
                  aria-label="Toggle formatting toolbar"
                />
                <EmojiPickerButton onSelect={handleEmojiInsert}>
                  {({ onClick }) => (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={onClick}
                      icon={<Smile />}
                      title="Emoji"
                      aria-label="Open emoji picker"
                    />
                  )}
                </EmojiPickerButton>
              </div>
              <Button
                type="button"
                onClick={handleSend}
                disabled={disabled || !editor?.getText().trim()}
                size="sm"
                icon={<Send />}
                title="Send message"
              >
                <kbd className="font-mono text-xs opacity-80">[↵]</kbd>
              </Button>
            </div>
          )}
        />
      </div>
    </div>
  );
}
